import { useState } from "react";

import { Loader, Tab } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";

import ImpactStatementView from "./ImpactStatementView";
import { StageSegment } from "./StageSegments";
import { impactStageDefinitions, impactStageOrder } from "./statementUtils";
import { useImpactStatements } from "./useImpactStatements";

type ImpactStatementProps = {
  ideaId: string;
  forStage?: OpenAPI.Schemas["ImpactStatement"]["stage"];
  restrictStagesTo?: OpenAPI.Schemas["ImpactStatement"]["stage"][];
  editable?: boolean;
};

export const ImpactStatementEditor = ({
  ideaId,
  forStage,
  restrictStagesTo: restrictStagesToProp,
  editable,
}: ImpactStatementProps) => {
  const {
    impactStatements,
    statementDefinition,
    isLoading,
    canManageIdea,
    restrictStagesTo: defaultRestrictStagesTo,
  } = useImpactStatements(ideaId);

  const availableStages = restrictStagesToProp ?? defaultRestrictStagesTo;
  const existingStages = Object.keys(
    impactStatements,
  ) as OpenAPI.Schemas["ImpactStatement"]["stage"][];
  const [stage, setStage] = useState(forStage || existingStages[0]);

  if (isLoading) {
    return <Loader active inline="centered" />;
  }

  if (!statementDefinition) {
    return null;
  }

  if (availableStages && availableStages.length === 1) {
    const onlyStage = availableStages[0]!;

    return (
      <ImpactStatementView
        ideaId={ideaId}
        stage={onlyStage}
        editable={editable}
        rangeInputType={impactStageDefinitions[onlyStage].rangeInputType}
        showPreviousStatement
      />
    );
  }

  return (
    <Tab
      activeIndex={impactStageOrder.indexOf(stage!)}
      onTabChange={(_, data) => {
        const newStage = impactStageOrder[data.activeIndex as number];
        setStage(newStage);
      }}
      menu={{ secondary: true, pointing: true }}
      panes={impactStageOrder
        .filter((s) => !availableStages || availableStages.includes(s))
        .map((s) => ({
          menuItem: impactStageDefinitions[s].name,
          render: () => {
            const canEditStage =
              editable || (canManageIdea && s !== "userEstimate");

            if (s !== "userEstimate" && !impactStatements[s]) {
              return <StageSegment ideaId={ideaId} stage={s} />;
            }
            return (
              <ImpactStatementView
                ideaId={ideaId}
                stage={s}
                editable={canEditStage}
                rangeInputType={impactStageDefinitions[s].rangeInputType}
                showPreviousStatement={canEditStage}
              />
            );
          },
        }))}
    />
  );
};
